"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useMemo } from "react";
import { Navbar } from "@/components/layout/navbar";
import { Footer } from "@/components/layout/footer";
import { BatchCountdown } from "@/components/batch-countdown";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { listCourseRecords } from "@/lib/courses-catalog";
import { formatBatchDate, getNextBatchStart } from "@/lib/batch-schedule";
import { GST_PRICE_NOTE } from "@/lib/site-config";

export default function UpcomingBatchPage() {
  const router = useRouter();
  const rows = listCourseRecords();
  const nextBatchDate = useMemo(() => formatBatchDate(getNextBatchStart()), []);

  return (
    <div className="site-page">
      <Navbar />

      <main className="relative z-10 flex-1 pt-20">
        <section className="site-section relative overflow-hidden bg-background py-16 md:py-24">
          <div className="pointer-events-none absolute inset-0 z-0 bg-grid opacity-50 mask-image-gradient dark:opacity-20" />

          <div className="site-container relative z-10 w-full">
            <div className="mb-10 grid w-full items-start gap-8 lg:grid-cols-[1fr_minmax(280px,360px)] lg:gap-10 xl:grid-cols-[1fr_380px]">
              <div className="min-w-0">
                <Badge variant="outline" className="mb-4 border-mont-violet/30 text-mont-violet">
                  Upcoming batch
                </Badge>
                <h1 className="mb-4 font-heading text-4xl font-bold text-foreground md:text-5xl xl:text-6xl">
                  Next intake & schedules
                </h1>
                <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
                  Batch timings rotate based on classroom capacity. Use WhatsApp or the contact page — we&apos;ll confirm the
                  upcoming slot for the exact program you want. All amounts below match the brochure INR structure site-wide.
                </p>
                <p className="text-sm text-muted-foreground">{GST_PRICE_NOTE}</p>
              </div>

              <div className="w-full shrink-0 lg:sticky lg:top-28">
                <BatchCountdown compact />
              </div>
            </div>

            <div className="mb-10 flex w-full justify-start">
              <Link
                href="https://docs.google.com/forms/d/e/1FAIpQLSdb0R9sj-InA7QxbbBjshD8fHR97DvO8ZialyalxWGwDS8OGw/viewform?usp=send_form&pli=1&authuser=0"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="group relative h-14 overflow-hidden rounded-full border-0 bg-gradient-to-r from-mont-violet via-fuchsia-600 to-mont-violet bg-[length:200%_auto] px-8 text-white shadow-[0_0_40px_rgba(139,92,246,0.6)] transition-all duration-300 animate-gradient hover:scale-105 sm:h-16 sm:px-10">
                  <div className="absolute inset-0 flex h-full w-full justify-center [transform:skew(-12deg)_translateX(-150%)] group-hover:duration-1000 group-hover:[transform:skew(-12deg)_translateX(150%)]">
                    <div className="relative h-full w-12 bg-white/30 blur-sm" />
                  </div>
                  <span className="relative flex items-center gap-2 text-sm font-bold uppercase tracking-wide sm:text-lg">
                    Apply via Google Form
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-arrow-right transition-transform group-hover:translate-x-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                  </span>
                </Button>
              </Link>
            </div>

            <div className="w-full md:hidden space-y-4">
              {rows.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => router.push(`/courses/${r.id}`)}
                  className="w-full rounded-2xl border border-border bg-card/60 p-4 text-left transition-colors hover:border-mont-violet/40 hover:bg-card/80 active:scale-[0.99]"
                >
                  <p className="font-semibold text-foreground">{r.title}</p>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Duration</p>
                      <p className="text-foreground">{r.duration}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Next batch</p>
                      <p className="font-semibold text-mont-violet" suppressHydrationWarning>{nextBatchDate}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">One-time</p>
                      <p className="text-foreground">{r.priceMain}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">EMI (3M)</p>
                      <p className="text-muted-foreground">{r.emi ?? "—"}</p>
                    </div>
                  </div>
                  {r.priceCompareNote ? (
                    <p className="mt-2 text-xs text-muted-foreground">{r.priceCompareNote}</p>
                  ) : null}
                </button>
              ))}
            </div>

            <div className="hidden md:block w-full overflow-hidden rounded-3xl border border-border bg-card/40 backdrop-blur-md">
              <div className="w-full overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-muted/60">
                    <tr className="text-left">
                      <th className="px-4 py-4 font-semibold text-foreground sm:px-6">Program</th>
                      <th className="px-4 py-4 font-semibold text-foreground sm:px-6">Duration</th>
                      <th className="px-4 py-4 font-semibold text-foreground sm:px-6">Next Batch</th>
                      <th className="px-4 py-4 font-semibold text-foreground sm:px-6">One-time (INR)</th>
                      <th className="px-4 py-4 font-semibold text-foreground sm:px-6">EMI (3M)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.id}
                        onClick={() => router.push(`/courses/${r.id}`)}
                        className="cursor-pointer border-b border-border/80 transition-colors hover:bg-card/80"
                      >
                        <td className="px-4 py-4 font-medium text-foreground sm:px-6">{r.title}</td>
                        <td className="px-4 py-4 text-muted-foreground sm:px-6">{r.duration}</td>
                        <td className="whitespace-nowrap px-4 py-4 font-semibold text-mont-violet sm:px-6" suppressHydrationWarning>
                          {nextBatchDate}
                        </td>
                        <td className="px-4 py-4 text-foreground sm:px-6">
                          {r.priceMain}
                          {r.priceCompareNote ? (
                            <span className="mt-1 block text-xs text-muted-foreground">{r.priceCompareNote}</span>
                          ) : null}
                        </td>
                        <td className="px-4 py-4 text-muted-foreground sm:px-6">{r.emi ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
